import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import { formatToPhone } from '../../../../../helpers/format'

import { useBranding } from '../../../../../hooks/branding'
import { usePrequalRequestAsync } from '../../../../../hooks/requests'

import { If, Unless, Text, Button, Avatar, CircleIcon } from '../../../../../components'

import styles from './PrequalPrompt.module.scss'

const PrequalPrompt = ({ onClose, className, ...restProps }) => {
  const {
    customerFirstName,
    sponsorFirstName,
    sponsorFullName,
    customerPhotoUri,
    sponsorPhotoUri,
    customerPhone,
    sponsorPhone,
    customerEmail,
    sponsorEmail,
    customerType,
  } = useBranding()

  const {
    execute: sendPrequalRequest,
    isLoading,
    isSuccess,
    promptRetry,
  } = usePrequalRequestAsync()

  const lenderPhoto =
    customerType === 'RealEstateAgent' ? sponsorPhotoUri : customerPhotoUri
  const contactFirstName = sponsorFirstName || customerFirstName
  const contactEmail = sponsorEmail || customerEmail
  const contactPhone = sponsorPhone || customerPhone
  const requestComplete = isSuccess || promptRetry
  const needsLenderPhoto = customerType === 'RealEstateAgent' && lenderPhoto

  const mainTextProps = { size: 'l', color: 'white' }
  const subTextProps = { size: 's', color: 'gray' }

  const handleOnClose = () => {
    if (onClose) {
      onClose()
    }
  }

  return (
    <div className={classnames(styles.wrapper, className)} {...restProps}>
      <div className={styles.curtain} onClick={handleOnClose} />
      <div className={styles.content} onClick={e => e.stopPropagation()}>
        <div className={styles.avatarWrap}>
          <Unless condition={promptRetry}>
            <If condition={customerType === 'LoanOfficer' || !requestComplete}>
              <Avatar className={styles.avatar} src={customerPhotoUri} />
            </If>
            <If condition={needsLenderPhoto}>
              <Avatar className={styles.avatar} src={lenderPhoto} />
            </If>
          </Unless>
          <If condition={promptRetry}>
            <CircleIcon
              className={classnames(styles.avatar, styles.errored)}
              icon='homebot'
              backgroundColor='none'
            />
          </If>
        </div>
        <Unless condition={requestComplete}>
          <>
            <Text {...mainTextProps}>Verify your price point</Text>
            <If condition={sponsorFullName}>
              <Text {...subTextProps}>
                {customerFirstName} trusts lender {sponsorFullName} to help
                answer your questions.
              </Text>
            </If>
            <Text {...subTextProps}>
              Chat with {contactFirstName} about your goals, how to be
              competitive in the market, and getting a prequalification.
            </Text>
            <Button
              primary
              fullWidth
              loading={isLoading}
              onClick={sendPrequalRequest}
            >
              Verify with {contactFirstName}
            </Button>
            <Button minimal fullWidth onClick={handleOnClose}>
              Maybe later
            </Button>
          </>
        </Unless>
        <If condition={requestComplete}>
          <>
            <Text {...mainTextProps}>
              {isSuccess &&
                `${contactFirstName} has been notified and will reach out soon!`}
              {promptRetry && `Something went wrong!`}
            </Text>
            <Text {...subTextProps}>
              {promptRetry && 'Please retry again in a few minutes.'}
              {promptRetry && <br />}
              Can’t wait?
              <Text element='a' color='blue' href={`mailto:${contactEmail}`}>
                {` Email ${contactFirstName} `}
              </Text>
              or call at
              <br />
              {formatToPhone(contactPhone)}
            </Text>
            <If condition={isSuccess}>
              <Button primary fullWidth onClick={handleOnClose}>
                Sounds good
              </Button>
            </If>
            <If condition={promptRetry}>
              <Button
                primary
                fullWidth
                loading={isLoading}
                onClick={sendPrequalRequest}
              >
                Retry
              </Button>
              <Button minimal fullWidth onClick={handleOnClose}>
                Maybe later
              </Button>
            </If>
          </>
        </If>
      </div>
    </div>
  )
}

PrequalPrompt.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
}

export default PrequalPrompt
